/**
 * Pagos a Incautadores (ares5085).
 *
 * Rediseño 2026-10-28: el pago se gana AL INCAUTAR (no espera a que el
 * cliente pague el incauto) y se paga POR INCAUTADOR: se elige uno del
 * catálogo, salen solo sus incautos pendientes con el monto sugerido por su
 * tarifa (fija o %, tope el valor del incauto), se marcan los que se pagan
 * y se indica la forma de pago (efectivo, transferencia o cheque). El
 * recibo sale a nombre del catálogo. Backend: ares5085backend.py.
 *
 * Sin catálogo de incautadores (varinicial.catalogoincautadores apagado)
 * hay además el modo "Por préstamo": se busca el préstamo, salen sus
 * incautos pendientes aunque no tengan la cédula del incautador, y el
 * nombre/cédula de quien cobra se escriben (precargados del incauto).
 */
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, FpDataGrid, StatusPill, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { useMultiempresa, SelectorEmpresa, nombreEmpresa } from "lib/multiempresa";
import { encabezadoEmpresaHtml } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const digitos = (v) => String(v || "").replace(/\D/g, "");

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export const FORMAS_PAGO = ["Efectivo", "Transferencia", "Cheque"];

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}
SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

/**
 * Recibo del pago al incautador (impresión con iframe oculto). Lo usan el
 * registro y la reimpresión del listado.
 */
export function imprimirRecibo(recibo) {
  const lineas = (recibo.lineas || [])
    .map(
      (l) => `<tr>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${esc(l.chasis)}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${esc(
          [l.marca, l.modelo].filter(Boolean).join(" ")
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          l.valor
        )}</td>
      </tr>`
    )
    .join("");
  const forma = recibo.formapago || "Efectivo";
  const detalleForma =
    forma !== "Efectivo" && (recibo.banco || recibo.referencia)
      ? ` · ${esc(recibo.banco)} ${esc(recibo.referencia)}`
      : "";
  const anulado = recibo.anulado
    ? `<p style="text-align:center;color:#b00020;font-weight:bold;border:2px solid #b00020;padding:4px">
         ANULADO${recibo.razonanula ? ` — ${esc(recibo.razonanula)}` : ""}</p>`
    : "";
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Recibo Pago Incautador #${esc(
      recibo.numero
    )}</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:420px;margin:0 auto">
      ${encabezadoEmpresaHtml(recibo.compania)}
      <p style="text-align:center;margin:0 0 12px;font-weight:bold">Recibo de Pago a Incautador #${esc(
        recibo.numero
      )}</p>
      ${anulado}
      <p style="margin:2px 0"><b>Fecha:</b> ${esc(recibo.fecha)}</p>
      <p style="margin:2px 0"><b>Cédula:</b> ${esc(recibo.cedula)}</p>
      <p style="margin:2px 0"><b>Nombres:</b> ${esc(recibo.nombres)}</p>
      <p style="margin:2px 0"><b>Forma de pago:</b> ${esc(forma)}${detalleForma}</p>
      ${
        recibo.concepto
          ? `<p style="margin:2px 0"><b>Concepto:</b> ${esc(recibo.concepto)}</p>`
          : ""
      }
      <table style="width:100%;border-collapse:collapse;margin-top:10px">
        <thead><tr style="background:#eee">
          <th style="padding:4px 6px;text-align:left">Chasis</th>
          <th style="padding:4px 6px;text-align:left">Vehículo</th>
          <th style="padding:4px 6px;text-align:right">Valor</th>
        </tr></thead>
        <tbody>${lineas}</tbody>
      </table>
      <p style="text-align:right;font-weight:bold;margin-top:10px">Total General: RD$ ${fmtMoney(
        recibo.total
      )}</p>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Firma del incautador</p>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

const PILL_ESTADO = {
  Incautado: "warning",
  "Cobrado al cliente": "success",
  Cerrado: "neutral",
};

export default function PagosIncautadores({ embedded, onExit, onSaved }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  // ---- Pendientes de todos (para el selector) ----
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const multiempresa = useMultiempresa();
  const [empresaFiltro, setEmpresaFiltro] = useState("0");
  // Con catálogo solo se paga por incautador; sin él, también por préstamo.
  const [catalogo, setCatalogo] = useState(true);
  const [modo, setModo] = useState("incautador"); // incautador | prestamo

  const cargar = (empresaSel = empresaFiltro) => {
    setLoading(true);
    return post("/api/pagoincautador_pendientes", {
      ...(multiempresa.activo ? { empresa: Number(empresaSel) } : {}),
    })
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data.pendientes)) {
          setPendientes(data.pendientes);
          setCatalogo(data.catalogo !== false);
          if (data.catalogo !== false) setModo("incautador");
        } else
          showAlert({
            message: data.Error || "No se pudieron cargar los pendientes",
            color: "error",
          });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Incautadores con algo pendiente (agrupados por cédula).
  const incautadores = useMemo(() => {
    const mapa = new Map();
    pendientes.forEach((p) => {
      if (p.sincedula) return;
      const k = digitos(p.cedincautador);
      const g = mapa.get(k) || {
        clave: k,
        cedula: p.cedincautador,
        nombre: p.incautador,
        cantidad: 0,
        total: 0,
      };
      g.cantidad += 1;
      g.total += Number(p.sugerido) || 0;
      mapa.set(k, g);
    });
    return Array.from(mapa.values()).sort((a, b) => b.total - a.total);
  }, [pendientes]);
  const sinCedula = pendientes.filter((p) => p.sincedula).length;

  // ---- Selección ----
  const [incautador, setIncautador] = useState(null);
  const [seleccion, setSeleccion] = useState([]);
  const [montos, setMontos] = useState({}); // id -> texto
  const [formapago, setFormapago] = useState("Efectivo");
  const [banco, setBanco] = useState("");
  const [referencia, setReferencia] = useState("");
  const [concepto, setConcepto] = useState("");

  // ---- Por préstamo (sin catálogo) ----
  const [prestamo, setPrestamo] = useState("");
  const [nombrePago, setNombrePago] = useState("");
  const [cedulaPago, setCedulaPago] = useState("");
  const porPrestamo = !catalogo && modo === "prestamo";

  // Préstamos con algo pendiente, para el buscador (número, cliente o chasis).
  const prestamosPendientes = useMemo(() => {
    const mapa = new Map();
    pendientes.forEach((p) => {
      const k = String(p.nuprest);
      const g = mapa.get(k) || {
        clave: k,
        cliente: p.cliente,
        chasis: [],
        incautador: p.incautador,
        cantidad: 0,
        valor: 0,
      };
      g.chasis.push(p.chasis);
      g.cantidad += 1;
      g.valor += Number(p.valor) || 0;
      mapa.set(k, g);
    });
    return Array.from(mapa.values());
  }, [pendientes]);
  const prestamoElegido = prestamosPendientes.find((o) => o.clave === prestamo.trim()) || null;

  const delPrestamo = useMemo(() => {
    const n = prestamo.trim();
    return n ? pendientes.filter((p) => String(p.nuprest) === n) : [];
  }, [pendientes, prestamo]);

  const buscarPrestamo = (texto) => {
    setPrestamo(texto);
    const n = texto.trim();
    const lista = n ? pendientes.filter((p) => String(p.nuprest) === n) : [];
    setSeleccion(lista.map((p) => p.id));
    setMontos(Object.fromEntries(lista.map((p) => [p.id, String(p.sugerido)])));
    setNombrePago(lista[0] ? lista[0].incautador || "" : "");
    setCedulaPago(lista[0] ? lista[0].cedincautador || "" : "");
    if (lista.length && !concepto) setConcepto("Pago de incautos");
  };

  const suyos = useMemo(() => {
    if (porPrestamo) return delPrestamo;
    return incautador
      ? pendientes.filter((p) => digitos(p.cedincautador) === incautador.clave)
      : [];
  }, [pendientes, incautador, porPrestamo, delPrestamo]);

  const elegir = (inc) => {
    setIncautador(inc);
    const lista = inc ? pendientes.filter((p) => digitos(p.cedincautador) === inc.clave) : [];
    setSeleccion(lista.map((p) => p.id));
    setMontos(Object.fromEntries(lista.map((p) => [p.id, String(p.sugerido)])));
    if (inc && !concepto) setConcepto("Pago de incautos");
  };

  const monto = (p) => toNum(montos[p.id] ?? p.sugerido);
  const total = suyos.filter((p) => seleccion.includes(p.id)).reduce((s, p) => s + monto(p), 0);

  const cambiarMonto = (p, v) => {
    if (toNum(v) > toNum(p.valor)) {
      showAlert({
        message: "El valor a pagar no puede pasar del valor del incauto",
        color: "warning",
      });
      setMontos((m) => ({ ...m, [p.id]: String(p.valor) }));
      return;
    }
    setMontos((m) => ({ ...m, [p.id]: v }));
  };

  // ---- Guardar ----
  const [guardando, setGuardando] = useState(false);

  const limpiar = () => {
    setIncautador(null);
    setPrestamo("");
    setNombrePago("");
    setCedulaPago("");
    setSeleccion([]);
    setMontos({});
    setFormapago("Efectivo");
    setBanco("");
    setReferencia("");
    setConcepto("");
  };

  const guardar = async () => {
    if (porPrestamo ? !nombrePago.trim() : !incautador) {
      showAlert({
        message: porPrestamo ? "Indique el nombre del incautador" : "Seleccione el incautador",
        color: "warning",
      });
      return;
    }
    const items = suyos
      .filter((p) => seleccion.includes(p.id))
      .map((p) => ({ id: p.id, valor: monto(p) }));
    if (!items.length) {
      showAlert({ message: "Marque al menos un incauto", color: "warning" });
      return;
    }
    if (items.some((i) => !(i.valor > 0))) {
      showAlert({ message: "Hay incautos marcados con valor en 0", color: "warning" });
      return;
    }
    if (formapago !== "Efectivo" && (!banco.trim() || !referencia.trim())) {
      showAlert({
        message: `Indique el banco y la referencia del ${formapago.toLowerCase()}`,
        color: "warning",
      });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/pagoincautador_guardar", {
        ...(porPrestamo
          ? { porprestamo: true, nombre: nombrePago.trim(), cedula: cedulaPago.trim() }
          : { cedula: incautador.cedula }),
        concepto: concepto.trim(),
        formapago,
        banco: banco.trim(),
        referencia: referencia.trim(),
        items,
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Pago registrado", color: "success" });
        if (data.recibo) imprimirRecibo(data.recibo);
        limpiar();
        cargar();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "Error al registrar el pago", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const columnas = [
    { field: "fecha", headerName: "Incautado", width: 100 },
    ...(multiempresa.activo
      ? [
          {
            field: "empresa",
            headerName: "Empresa",
            width: 130,
            valueGetter: (_v, row) => nombreEmpresa(multiempresa.empresas, row.empresa || 1),
          },
        ]
      : []),
    { field: "nuprest", headerName: "Préstamo", width: 90 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 150 },
    { field: "chasis", headerName: "Chasis", width: 160 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      width: 140,
      valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
    },
    {
      field: "estadotexto",
      headerName: "Incauto",
      width: 150,
      renderCell: ({ row }) => (
        <StatusPill label={row.estadotexto} variant={PILL_ESTADO[row.estadotexto] || "neutral"} />
      ),
    },
    {
      field: "valor",
      headerName: "Valor incauto",
      type: "number",
      width: 115,
      valueFormatter: (v) => fmtMoney(v),
    },
    {
      field: "apagar",
      headerName: "A pagar",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <TextField
          size="small"
          type="number"
          value={montos[row.id] ?? String(row.sugerido)}
          disabled={!seleccion.includes(row.id)}
          onChange={(e) => cambiarMonto(row, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          inputProps={{ style: { textAlign: "right" }, min: 0 }}
          sx={{ ...fpInputSx, mt: 0.4 }}
        />
      ),
    },
  ];

  let tituloLista = "Incautos pendientes";
  if (porPrestamo && prestamo.trim()) tituloLista = `Incautos del préstamo ${prestamo.trim()}`;
  else if (!porPrestamo && incautador) tituloLista = `Incautos de ${incautador.nombre}`;
  let vacioTitulo = "Seleccione un incautador";
  if (porPrestamo) {
    vacioTitulo = prestamo.trim() ? "Sin incautos pendientes" : "Escriba el número del préstamo";
  } else if (incautador) vacioTitulo = "Sin incautos pendientes";

  const tarifaTexto = (() => {
    const p = suyos[0];
    if (!p) return "";
    if (p.tipotarifa === "F" && p.tarifa > 0)
      return `Tarifa: RD$ ${fmtMoney(p.tarifa)} por incauto`;
    if (p.tipotarifa === "P" && p.tarifa > 0) return `Tarifa: ${p.tarifa}% del valor del incauto`;
    return "Sin tarifa: se sugiere el valor del incauto";
  })();

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Pagos a Incautadores"
        subtitle="El incautador se gana el pago al incautar; se le paga por sus incautos pendientes"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={guardar}
              disabled={guardando || (!porPrestamo && !incautador) || !seleccion.length}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar y pagar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Incautador */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.info}>
            Incautador
          </SectionTitle>
          <Grid container spacing={2} alignItems="center">
            {multiempresa.activo && (
              <Grid item xs={12} sm={4} md={3}>
                <SelectorEmpresa
                  value={empresaFiltro}
                  onChange={(v) => {
                    setEmpresaFiltro(v);
                    limpiar();
                    cargar(v);
                  }}
                  empresas={multiempresa.empresas}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            {!catalogo && (
              <Grid item xs={12}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={modo}
                  onChange={(_, v) => {
                    if (!v) return;
                    limpiar();
                    setModo(v);
                  }}
                >
                  <ToggleButton value="incautador" sx={{ textTransform: "none", px: 2 }}>
                    Por incautador
                  </ToggleButton>
                  <ToggleButton value="prestamo" sx={{ textTransform: "none", px: 2 }}>
                    Por préstamo
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}
            {porPrestamo ? (
              <>
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    options={prestamosPendientes}
                    value={prestamoElegido}
                    loading={loading}
                    onChange={(_, v) => buscarPrestamo(v ? v.clave : "")}
                    getOptionLabel={(o) => `${o.clave} — ${o.cliente} — ${o.chasis.join(", ")}`}
                    isOptionEqualToValue={(o, v) => o.clave === v.clave}
                    renderOption={(props, o) => (
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      <li {...props} key={o.clave}>
                        <Box sx={{ width: "100%" }}>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.88rem" }}>
                            {o.clave} · {o.cliente}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: FP.color.textMuted }}>
                            {o.chasis.join(", ")} · {o.incautador || "Sin incautador"} · RD${" "}
                            {fmtMoney(o.valor)}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    noOptionsText="Ningún préstamo con incautos pendientes de pago"
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        label="Préstamo, cliente o chasis"
                        helperText=" "
                        sx={fpInputSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={8} md={4}>
                  <TextField
                    fullWidth
                    label="Nombre del incautador"
                    value={nombrePago}
                    onChange={(e) => setNombrePago(e.target.value)}
                    inputProps={{ maxLength: 45 }}
                    helperText=" "
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Cédula (opcional)"
                    value={cedulaPago}
                    onChange={(e) => setCedulaPago(e.target.value)}
                    inputProps={{ maxLength: 15 }}
                    helperText=" "
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            ) : null}
            <Grid item xs={12} md={6} sx={porPrestamo ? { display: "none" } : undefined}>
              <Autocomplete
                options={incautadores}
                value={incautador}
                loading={loading}
                onChange={(_, v) => elegir(v)}
                getOptionLabel={(o) => `${o.nombre} — ${o.cedula}`}
                isOptionEqualToValue={(o, v) => o.clave === v.clave}
                renderOption={(props, o) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <li {...props} key={o.clave}>
                    <Box sx={{ width: "100%" }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.88rem" }}>
                        {o.nombre}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: FP.color.textMuted }}>
                        {o.cedula} · {o.cantidad} pendiente(s) · RD$ {fmtMoney(o.total)}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="Ningún incautador tiene incautos pendientes de pago"
                renderInput={(params) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <TextField {...params} label="Incautador a pagar" sx={fpInputSx} />
                )}
              />
            </Grid>
            {incautador && !porPrestamo ? (
              <Grid item xs={12} md>
                <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                  {tarifaTexto}
                </Typography>
              </Grid>
            ) : null}
          </Grid>
          {sinCedula > 0 && !porPrestamo ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {catalogo
                ? `${sinCedula} incauto(s) pendiente(s) no tienen la cédula del incautador y no se pueden pagar desde aquí.`
                : `${sinCedula} incauto(s) pendiente(s) no tienen la cédula del incautador: páguelos con "Por préstamo".`}
            </Alert>
          ) : null}
        </Box>

        {/* Incautos del incautador */}
        <Box sx={sectionBox}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <SectionTitle icon={ListAltOutlinedIcon} color={FP.color.accentBlue}>
              {tituloLista}
            </SectionTitle>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
              {seleccion.length} marcados · Total: RD$ {fmtMoney(total)}
            </Typography>
          </Stack>
          <Box sx={{ height: 420, bgcolor: "#fff" }}>
            <FpDataGrid
              rows={suyos}
              columns={columnas}
              loading={loading}
              rowHeight={52}
              checkboxSelection
              rowSelectionModel={seleccion}
              onRowSelectionModelChange={(m) => setSeleccion(m)}
              vacioIcono={InboxOutlinedIcon}
              vacioTitulo={vacioTitulo}
              vacioSubtitulo={
                porPrestamo || incautador
                  ? undefined
                  : "Salen sus incautos sin pagar, con el monto de su tarifa."
              }
            />
          </Box>
        </Box>

        {/* Forma de pago */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
            Forma de pago
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                label="Forma de pago"
                value={formapago}
                onChange={(e) => setFormapago(e.target.value)}
                sx={fpInputSx}
              >
                {FORMAS_PAGO.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {formapago !== "Efectivo" ? (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Banco"
                    value={banco}
                    onChange={(e) => setBanco(e.target.value)}
                    inputProps={{ maxLength: 100 }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={formapago === "Cheque" ? "No. de cheque" : "Referencia"}
                    value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                    inputProps={{ maxLength: 100 }}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            ) : null}
            <Grid item xs={12} sm={formapago !== "Efectivo" ? 3 : 9}>
              <TextField
                fullWidth
                label="Concepto"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
                inputProps={{ maxLength: 250 }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
          {formapago === "Efectivo" ? (
            <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted, mt: 1 }}>
              El efectivo sale de la caja de su sucursal en el cuadre.
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

PagosIncautadores.propTypes = {
  embedded: PropTypes.bool,
  onExit: PropTypes.func,
  onSaved: PropTypes.func,
};
PagosIncautadores.defaultProps = { embedded: false, onExit: null, onSaved: null };
